import {
    Box,
    Button,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Radio,
    RadioGroup,
    Stack,
    Text,
    VStack,
} from '@chakra-ui/react';
import { ComponentProps } from 'react';
import { startOfTomorrow } from 'date-fns';
import { PropertyKeyAvailableType, PropertyManager } from '@acp/shared';
import { Controller, useFormContext } from 'react-hook-form';
import { DatePicker, RestrictedLengthTextAreaField, ThemeTokens } from '@acp/core-ui';
import { IntegratedPropertyDialog } from '../integrated-property-dialog';
import { PropertyAddressCard } from '../property-address-card';
import { EditPropertyFormValues } from './form-schema';
import { EditIntegratedPropertyFormValues } from './integrated-form-schema';

function EditIntegratedPropertyScreen({
    propertyId,
    propertyManager,
    keyNumber,
    keyLocationId,
    isKeyAvailable,
    keyPickupLocationFullAddress,
    onSubmit,
    onClose,
    showHeader = true,
    showCloseButton = true,
    p,
    showAgencyKeyFields = true,
    showOnsiteKeyFields = true,
}: {
    propertyId: number;
    propertyManager: PropertyManager | null;
    keyNumber: string | null;
    keyLocationId: string | null;
    isKeyAvailable: boolean;
    keyPickupLocationFullAddress: string;
    onSubmit: (vals: EditPropertyFormValues) => void;
    onClose: () => void;
    showHeader?: boolean;
    showCloseButton?: boolean;
    p?: ComponentProps<typeof ModalBody>['p'];
    showAgencyKeyFields: boolean;
    showOnsiteKeyFields: boolean;
}) {
    const {
        handleSubmit,
        watch,
        control,
    } = useFormContext<EditIntegratedPropertyFormValues>();

    const currentFormValues = watch();

    return (
        <VStack align="left">
            { showHeader && <ModalHeader>Edit property</ModalHeader> }
            <ModalBody p={p}>
                { showHeader && <PropertyAddressCard w="full" mb={5} propertyId={propertyId} /> }

                <VStack align="left" gap={6}>

                    {showAgencyKeyFields
                    && (
                        <>
                            <IntegratedPropertyDialog
                                title="Key details for this property is integrated with your property management software"
                            />
                            <FormControl>
                                <VStack align="left" gap={6} pb={4}>
                                    <Box>
                                        <FormLabel>Property manager</FormLabel>
                                        <Text>{propertyManager !== null ? propertyManager.name : 'Unknown'}</Text>
                                    </Box>

                                    <Box>
                                        <FormLabel>Key number</FormLabel>
                                        <Text>{keyNumber ?? 'Unknown'}</Text>
                                        {keyLocationId === null && (
                                            <FormHelperText>
                                                This key can be picked up at this agency office
                                            </FormHelperText>
                                        )}
                                    </Box>

                                    <Box>
                                        <FormLabel>Is key cut & available?</FormLabel>
                                        <Text>{isKeyAvailable ? 'Yes' : 'No'}</Text>
                                    </Box>

                                    {isKeyAvailable && keyPickupLocationFullAddress !== null && (
                                        <Box>
                                            <FormLabel>Key pickup location</FormLabel>
                                            <Text>{keyPickupLocationFullAddress}</Text>
                                        </Box>
                                    )}
                                </VStack>
                            </FormControl>
                        </>
                    )}
                    {
                        showOnsiteKeyFields && (
                            <Box
                                w="full"
                                p={6}
                                borderColor={ThemeTokens.Defaults.borderColor}
                                borderWidth={1}
                                borderRadius="md"
                            >
                                <VStack gap={4}>
                                    <Controller
                                        control={control}
                                        name="propertyKey.onsiteAvailability"
                                        render={({ field, fieldState }) => (
                                            <FormControl
                                                isRequired
                                                isInvalid={fieldState.invalid}
                                                mb={2}
                                            >
                                                <FormLabel>
                                                    Is a key onsite at the property?
                                                </FormLabel>

                                                <RadioGroup {...field}>
                                                    <Stack direction="row" spacing={6}>
                                                        <Radio
                                                            value={
                                                                PropertyKeyAvailableType.Available
                                                            }
                                                        >
                                                            Yes
                                                        </Radio>
                                                        <Radio
                                                            value={
                                                                PropertyKeyAvailableType.Unavailable
                                                            }
                                                        >
                                                            No
                                                        </Radio>
                                                        <Radio
                                                            value={
                                                                PropertyKeyAvailableType.Temporary
                                                            }
                                                        >
                                                            Temporarily
                                                        </Radio>
                                                    </Stack>
                                                </RadioGroup>
                                                <FormErrorMessage>
                                                    {fieldState.error?.message}
                                                </FormErrorMessage>
                                            </FormControl>
                                        )}
                                    />
                                    {
                                        currentFormValues.propertyKey?.onsiteAvailability
                                            === PropertyKeyAvailableType.Temporary
                                            && (
                                                <Controller
                                                    control={control}
                                                    name="propertyKey.temporaryKeyExpirationDate"
                                                    render={({ field, fieldState }) => (
                                                        <FormControl
                                                            isInvalid={fieldState.invalid}
                                                            isRequired
                                                            mb={2}
                                                        >
                                                            <FormLabel>Expiration date</FormLabel>
                                                            <DatePicker
                                                                {...field}
                                                                placeholder="Select key expiration date"
                                                                isInvalid={fieldState.invalid}
                                                                dateDisplayFormat="do MMMM yyyy"
                                                                allowClear={false}
                                                                fromDate={startOfTomorrow()}
                                                            />
                                                            <FormErrorMessage>
                                                                {fieldState.error?.message}
                                                            </FormErrorMessage>
                                                        </FormControl>
                                                    )}
                                                />
                                            )
                                    }
                                    {
                                        currentFormValues.propertyKey?.onsiteAvailability
                                            !== PropertyKeyAvailableType.Unavailable
                                            && (
                                                <Controller
                                                    control={control}
                                                    name="propertyKey.keyOnsiteNotes"
                                                    render={({ fieldState }) => (
                                                        <FormControl
                                                            isInvalid={fieldState.invalid}
                                                            mb={2}
                                                        >
                                                            <RestrictedLengthTextAreaField
                                                                control={control}
                                                                name="propertyKey.keyOnsiteNotes"
                                                                label="Key onsite notes"
                                                                placeholder="Please explain how we can access this key onsite"
                                                                maxLength={500}
                                                                isRequired
                                                            />
                                                        </FormControl>
                                                    )}
                                                />
                                            )
                                    }
                                </VStack>
                            </Box>
                        )
                    }
                </VStack>
            </ModalBody>
            <ModalFooter p={p} justifyContent="space-between">
                {
                    showCloseButton && (
                        <Button
                            size="md"
                            colorScheme="muted"
                            variant="ghost"
                            onClick={onClose}
                        >
                            Close
                        </Button>
                    )
                }
                {
                    !showCloseButton && (
                        <Box />
                    )
                }
                <Button
                    colorScheme="secondary"
                    variant="solid"
                    size="md"
                    onClick={handleSubmit(onSubmit)}
                >
                    Next
                </Button>
            </ModalFooter>
        </VStack>
    );
}

export { EditIntegratedPropertyScreen };
